// Authenticated viewer display context for shell chrome. Unlike org branding, a person identity must never
// fall back to a compiled-in demo name: until the authenticated read lands (or if it fails) consumers render
// a neutral unavailable state. The server projection contains display fields only and no raw User id.

import { createContext, useContext, useEffect, useState } from 'react';
import type { UserDisplay } from './shell-identity.ts';
import { useSurfaceApi } from '../surfaces/SurfaceApiContext.tsx';

const ViewerIdentityContext = createContext<UserDisplay | null>(null);

/** The authenticated viewer projection, or null while unavailable. Never a guessed/shared person. */
export function useViewerIdentity(): UserDisplay | null {
  return useContext(ViewerIdentityContext);
}

/** Fetch the server-resolved viewer once at shell root and publish only a complete usable projection. */
export function ViewerIdentityProvider({
  children,
}: {
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const api = useSurfaceApi();
  const [viewer, setViewer] = useState<UserDisplay | null>(null);
  useEffect(() => {
    let live = true;
    void api
      .getViewerIdentity()
      .then((next) => {
        if (
          live &&
          next.name.trim() !== '' &&
          next.initials.trim() !== '' &&
          next.roleLabel.trim() !== ''
        ) {
          setViewer({ name: next.name, initials: next.initials, roleLabel: next.roleLabel });
        }
      })
      .catch(() => {
        // Identity is display-only here, but guessing a person would be dishonest. Keep the neutral state.
      });
    return () => {
      live = false;
    };
  }, [api]);
  return <ViewerIdentityContext.Provider value={viewer}>{children}</ViewerIdentityContext.Provider>;
}
